import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Check, Loader, Chrome } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExtensionConnect() {
  const { getToken, user } = useAuth();
  const [status, setStatus] = useState('connecting');

  useEffect(() => {
    const connect = async () => {
      if (!user) return;

      // The qudetv.js content script sets this the moment it's active on
      // this page -- if it's missing, the extension isn't installed (or
      // was just installed and hasn't loaded here yet), and postMessage
      // below would go nowhere. Showing "connected!" in that case was a
      // real bug: the user gets a false success with no working connection.
      if (localStorage.getItem('qude_ext_installed') !== '1') {
        setStatus('not_installed');
        return;
      }

      try {
        const clerkToken = await getToken();
        if (!clerkToken) { setStatus('error'); return; }

        // Exchange Clerk token for a long-lived extension token stored in MongoDB
        const res = await fetch(`${API}/extension/token`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${clerkToken}` },
        });
        if (!res.ok) { setStatus('error'); return; }
        const { token } = await res.json();

        // Send token to extension content script via postMessage
        window.postMessage({ type: 'QUDE_EXT_TOKEN', token }, '*');

        setTimeout(() => {
          setStatus('success');
          setTimeout(() => window.close(), 2000);
        }, 800);

      } catch (err) {
        setStatus('error');
      }
    };

    connect();
  }, [user, getToken]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#050508' }}>
      <div className="max-w-sm w-full text-center space-y-6">

        <img src="/logo.png" alt="Qude" className="w-12 h-12 object-contain mx-auto" />

        <div className="rounded-2xl border p-8 space-y-4" style={{
          background: 'rgba(255,255,255,0.03)',
          borderColor: status === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.08)',
        }}>

          {status === 'connecting' && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
                style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)' }}>
                <Loader className="w-8 h-8 text-violet-400 animate-spin" />
              </div>
              <p className="text-white font-bold text-lg" style={{ fontFamily: 'Outfit' }}>Connecting extension...</p>
              <p className="text-neutral-500 text-sm">Sending your credentials to the Qude extension</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
                style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}>
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <p className="text-white font-bold text-lg" style={{ fontFamily: 'Outfit' }}>Extension connected!</p>
              <p className="text-neutral-500 text-sm">Qude is now tracking your streams automatically. This tab will close shortly.</p>
            </>
          )}

          {status === 'not_installed' && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
                style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
                <Chrome className="w-8 h-8 text-red-400" />
              </div>
              <p className="text-white font-bold text-lg" style={{ fontFamily: 'Outfit' }}>Extension not detected</p>
              <p className="text-neutral-500 text-sm mb-4">Install the Qude Chrome extension first, then come back to this page.</p>
              <a href="/extension-token"
                className="block w-full py-3 rounded-xl font-semibold text-sm text-white text-center"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' }}>
                Get the Extension
              </a>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
                style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
                <span className="text-red-400 text-3xl font-bold">✕</span>
              </div>
              <p className="text-white font-bold text-lg" style={{ fontFamily: 'Outfit' }}>Something went wrong</p>
              <p className="text-neutral-500 text-sm mb-4">Make sure you're logged in to Qude and try again.</p>
              <button onClick={() => window.location.reload()}
                className="w-full py-3 rounded-xl font-semibold text-sm text-white"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' }}>
                Try Again
              </button>
            </>
          )}
        </div>

        {status === 'success' && (
          <p className="text-xs text-neutral-600">You can close this tab manually if it doesn't close automatically.</p>
        )}
      </div>
    </div>
  );
}
